import { createApi } from '@reduxjs/toolkit/query/react'

import {
  PaginationRequest,
  PaginationResponse,
} from '@/model/pagination'
import {
  readMineUser,
  RelatedSimpleUser,
  userSlice,
} from '@/redux/user'
import { baseQuery } from '@/redux/utils'

export interface Competition {
  id: number
  cover: string
  author: string
  title: string
  subtitle: string
  description: string
  note: string
  overview: string
  howToParticipate: string
  applicantLimit: number
  applicantCount: number
  competitorCount: number
  competitorLimit: number
  publishAt: number
  applyStartAt: number
  applyEndAt: number
  competitionStartAt: number
  competitionEndAt: number
  rewardAt: number
  isBetting: boolean
}

export interface CompetitionRanking {
  calmarRatio: number
  competitionId: number
  omegaRatio: number
  ranking: number
  sharpeRatio: number
  sortinoRatio: number
  totalBet: number
  myBet: number
  balance: number
  liveRedeem: number
  user: RelatedSimpleUser
  roi: number
  pnl: number
}

export interface ReadCompetitionRankingRequest extends PaginationRequest {
  id: number
  showMinePage?: boolean
}

export interface CompetitionStats {
  balance: number
  maxDrawDown: number
  pnl: number
  ranking: number
  roi: number
  sharpeRatio: number
  updatedAt: number
}

export interface CompetitionRankingResponse extends PaginationResponse<CompetitionRanking> {
  updatedAt: number
}

export interface ExportResponse {
  downloadUrl: string
}

export interface ImportCompetitorsRequest {
  file: File
  id: number
}

export interface ImportCompetitorsResponse {
  total: number
  successCount: number
  failureCount: number
}

export const readCompetitionRanking = 'readCompetitionRanking'
export const readAllCompetitions = 'readAllCompetitions'
export const readCompetitionMineStats = 'readCompetitionMineStats'

export const competitionSlice = createApi({
  reducerPath: 'competition',
  baseQuery,
  tagTypes: [readAllCompetitions, readCompetitionMineStats, readCompetitionRanking],
  endpoints: builder => ({
    readAllCompetitions: builder.query<Competition[], void>({
      query: () => '/competition/all',
      providesTags: [readAllCompetitions],
    }),
    readOneCompetition: builder.query<Competition, number>({
      query: (id) => `/competition/${id}`,
    }),
    updateOneCompetition: builder.mutation<void, Partial<Competition> & Pick<Competition, 'id'>>({
      query: (competition) => ({
        url: `/competition/${competition.id}`,
        method: 'PUT',
        body: competition,
      }),
      invalidatesTags: [readAllCompetitions],
    }),
    readCompetitionRanking:
      builder.query<CompetitionRankingResponse, ReadCompetitionRankingRequest>({
        query: ({ id, page, size, showMinePage }) => ({
          url: `/competition/${id}/ranking`,
          params: {
            page,
            size,
            showMinePage,
          },
        }),
        providesTags: [readCompetitionRanking],
      }),
    readCompetitionMineStats: builder.query<CompetitionStats, number>({
      query: (id) => `/competition/${id}/mine/stats`,
      providesTags: [readCompetitionMineStats],
    }),
    applyCompetition: builder.mutation<void, number>({
      query: (id) => ({
        url: `/competition/${id}/apply`,
        method: 'POST',
      }),
      extraOptions: {
        errorToast: false,
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(userSlice.util.invalidateTags([readMineUser]))
      },
    }),
    exportApplicants: builder.mutation<ExportResponse, number>({
      query: (id) => ({
        url: `/competition/${id}/applicant/export`,
        method: 'POST',
      }),
    }),
    exportRanking: builder.mutation<ExportResponse, number>({
      query: (id) => ({
        url: `/competition/${id}/ranking/export`,
        method: 'POST',
      }),
    }),
    importCompetitors: builder.mutation<ImportCompetitorsResponse, ImportCompetitorsRequest>({
      query: ({ id, file }) => {
        const formData = new FormData()
        formData.append('file', file)

        return {
          url: `/competition/${id}/competitor/import`,
          method: 'POST',
          body: formData,
        }
      },
      invalidatesTags: [readAllCompetitions],
    }),
  }),
})

export const {
  useReadAllCompetitionsQuery,
  useReadOneCompetitionQuery,
  useReadCompetitionMineStatsQuery,
  useApplyCompetitionMutation,
  useReadCompetitionRankingQuery,
  useExportApplicantsMutation,
  useImportCompetitorsMutation,
  useUpdateOneCompetitionMutation,
  useExportRankingMutation,
} = competitionSlice
